import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCategoriesWithProducts } from "../services/productService";
import { motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";
import { useCurrency } from "../hooks/useCurrency";
import { translateText, translateBatch } from '../services/translationService';

export default function Shop() {
  const [originalCategories, setOriginalCategories] = useState([]);
  const [displayCategories, setDisplayCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addToCart } = useCart();
  const { language } = useLanguage();
  const { formatPrice } = useCurrency();
  const [translatedTexts, setTranslatedTexts] = useState({});
  const [translating, setTranslating] = useState(false);
  const [dataLanguage, setDataLanguage] = useState('fr');

  // Configuration des URLs
    const API_BASE_URL = process.env.NODE_ENV === 'development'
    ? 'http://localhost/argan-shop'
    : 'https://d4unaturally.com';
  const REACT_BASE_URL = process.env.NODE_ENV === 'development'
    ? 'http://localhost:3000'
    : (process.env.PUBLIC_URL || '');


  // 🎯 NOUVELLE FONCTION : Trier les catégories (femme d'abord, puis homme)
  const sortCategoriesByGender = (categories) => {
    if (!categories || categories.length === 0) return [];
    
    // Séparer les catégories par genre
    const womenCategories = categories.filter(cat => cat.gender === "women");
    const menCategories = categories.filter(cat => cat.gender === "men");
    const otherCategories = categories.filter(cat => !cat.gender || (cat.gender !== "women" && cat.gender !== "men"));
    
    // Retourner dans l'ordre: femme, puis homme, puis autres
    return [...womenCategories, ...menCategories, ...otherCategories];
  };

  // ⚙️ Charger les produits
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log("🔄 Chargement des produits...");
        const data = await getCategoriesWithProducts();
        
        // Détection de la langue des données
        const detectedLang = detectDataLanguage(data);
        setDataLanguage(detectedLang);
        
        console.log(`📊 Langue détectée: ${detectedLang.toUpperCase()}`);
        console.log(`   Interface: ${language}`);
        
        // 🎯 TRI DES CATÉGORIES : femme d'abord, puis homme
        const sortedCategories = sortCategoriesByGender(data);
        console.log("🎯 Catégories triées:", {
          femmes: sortedCategories.filter(cat => cat.gender === "women").length,
          hommes: sortedCategories.filter(cat => cat.gender === "men").length,
          autres: sortedCategories.filter(cat => !cat.gender || (cat.gender !== "women" && cat.gender !== "men")).length
        });
        
        setOriginalCategories(sortedCategories);
        setDisplayCategories(sortedCategories);
      } catch (err) {
        console.error("❌ Error loading products:", err);
        setError("Unable to load products.");
      } finally {
        setLoading(false);
      }
    };

    const detectDataLanguage = (categories) => {
      if (!categories || categories.length === 0) return 'en';
      
      const languagePatterns = {
        fr: ['soins', 'cheveux', 'visage', 'corps', 'homme', 'femme', 'shampoing', 'après', 'huile', 'masque'],
        en: ['care', 'hair', 'face', 'body', 'men', 'women', 'shampoo', 'after', 'oil', 'mask']
      };
      
      const sampleTexts = [
        ...categories.slice(0, 3).map(cat => cat.name),
        ...categories.flatMap(cat => 
          cat.products.slice(0, 2).map(p => p.name)
        )
      ].join(' ').toLowerCase();
      
      let bestLanguage = 'en';
      let bestScore = 0;
      
      Object.entries(languagePatterns).forEach(([lang, words]) => {
        const score = words.filter(word => sampleTexts.includes(word)).length;
        if (score > bestScore) {
          bestScore = score;
          bestLanguage = lang;
        }
      });
      
      return bestLanguage;
    };
    
    fetchData();
  }, []);

  // 🌐 CORRECTION : Traduction de l'interface utilisateur
  useEffect(() => {
    const translateUI = async () => {
      // Textes sources en anglais
      const englishTexts = {
        title: "All Our Products",
        subtitle: "Discover our natural ranges for men and women 🌿",
        womenBannerTitle: "Women's Collection",
        womenBannerSubtitle: "Premium natural care for women",
        menBannerTitle: "Men's Collection",
        menBannerSubtitle: "Premium natural care for men",
        addButton: "Add to Cart",
        noProducts: "No products available in this category yet.",
        loading: "Loading products...",
        error: "Unable to load products.",
        translating: "Updating translations..."
      };

      // Si la langue est l'anglais, utiliser les textes originaux
      if (language === 'en') {
        console.log("✅ Langue EN - textes originaux");
        setTranslatedTexts(englishTexts);
        return;
      }

      console.log(`🎨 Traduction UI: en → ${language}`);
      const translated = {};

      try {
        for (const [key, value] of Object.entries(englishTexts)) {
          try {
            // FORCER la traduction depuis l'anglais
            const result = await translateText(value, language, 'en');
            translated[key] = result;
            console.log(`   🌐 ${key}: "${value}" → "${result}"`);
          } catch (error) {
            console.warn(`   ❌ Erreur ${key}:`, error);
            translated[key] = value; // Fallback
          }
        }
        setTranslatedTexts(translated);
      } catch (error) {
        console.error("💥 Erreur générale traduction UI:", error);
        setTranslatedTexts(englishTexts);
      }
    };

    translateUI();
  }, [language]);

  // 🧠 CORRECTION : Traduction des produits et catégories
  useEffect(() => {
    const translateProductsAndCategories = async () => {
      console.log("🔤 TRADUCTION DONNÉES", { 
        dataLang: dataLanguage,
        uiLang: language,
        categories: originalCategories.length
      });

      if (originalCategories.length === 0) return;

      // Si même langue, pas de traduction nécessaire
      if (dataLanguage === language) {
        console.log("✅ Même langue - pas de traduction");
        setDisplayCategories(originalCategories);
        return;
      }

      try {
        setTranslating(true);
        console.log(`🎯 Traduction: ${dataLanguage} → ${language}`);

        // TEST de connexion avec un texte simple
        const testText = "Hello";
        const testResult = await translateText(testText, language, 'en');
        console.log(`🧪 Test connexion: "${testText}" → "${testResult}"`);

        // Traduire les catégories
        const categoryNames = originalCategories.map(cat => cat.name);
        console.log(`📝 Catégories: ${categoryNames.slice(0, 2).join(', ')}...`);
        
        const translatedCategoryNames = await translateBatch(categoryNames, language, dataLanguage);
        console.log("✅ Catégories traduites");

        // Traduire les produits
        const allProductNames = originalCategories.flatMap(cat => 
          cat.products.map(p => p.name)
        );
        console.log(`📝 ${allProductNames.length} produits`);
        
        const translatedProductNames = await translateBatch(allProductNames, language, dataLanguage);
        console.log("✅ Produits traduits");

        // Reconstruction des données
        let productIndex = 0;
        const updatedCategories = originalCategories.map((category, catIndex) => ({
          ...category,
          name: translatedCategoryNames[catIndex] || category.name,
          products: category.products.map(product => ({
            ...product,
            name: translatedProductNames[productIndex++] || product.name
          }))
        }));

        // Vérification du résultat
        if (updatedCategories.length > 0) {
          console.log(`📋 Résultat: "${originalCategories[0].name}" → "${updatedCategories[0].name}"`);
        }

        setDisplayCategories(updatedCategories);
        console.log("🎉 Traduction données terminée");

      } catch (error) {
        console.error("💥 Erreur traduction données:", error);
        console.log("🔄 Utilisation des données originales");
        setDisplayCategories(originalCategories);
      } finally {
        setTranslating(false);
      }
    };

    if (originalCategories.length > 0) {
      translateProductsAndCategories();
    }
  }, [language, originalCategories, dataLanguage]);

  // 🛒 Ajouter au panier avec traduction
  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();

    addToCart({
      id: product.id,
      name: product.name,
      price: parseFloat(product.price),
      image: getProductImage(product),
      quantity: 1,
      originalCurrency: 'MAD'
    });

    // Notification avec texte traduit
    const button = e.target;
    const originalText = button.textContent;
    
    // Texte de confirmation selon la langue
    const confirmText = language === 'fr' ? '✓ Ajouté' : 
                       language === 'ar' ? '✓ تم الإضافة' : 
                       '✓ Added';
    
    button.textContent = confirmText;
    button.classList.add("bg-green-600");
    
    setTimeout(() => {
      button.textContent = originalText;
      button.classList.remove("bg-green-600");
    }, 2000);
  };

  // 🖼️ MÉTHODE 1 - Pour les anciens produits (images dans le dossier React)
  const getProductImagesOld = (product) => {
    const baseUrl = process.env.NODE_ENV === 'development' 
      ? 'http://localhost:3000'
      : (process.env.PUBLIC_URL || '');
    
    if (!product) return [`${baseUrl}/images/placeholder.jpg`];
    
    if (product.image_url) {
      return [product.image_url.startsWith('http') 
        ? product.image_url 
        : `${baseUrl}/images/products/${product.image_url.replace(/^\/+/, '')}`
      ];
    }
    
    if (product.images) {
      try {
        const imagesArray = JSON.parse(product.images.replace(/\\"/g, '"'));
        if (imagesArray.length > 0) {
          return imagesArray.map(img => 
            `${baseUrl}/images/products/${img.replace(/^\/+/, '').trim()}`
          );
        }
      } catch (error) {
        console.error("Error parsing images:", error);
      }
    }
    
    if (product.image) {
      return [`${baseUrl}/images/products/${product.image.replace(/^\/+/, '')}`];
    }
    
    return [`${baseUrl}/images/placeholder.jpg`];
  };

  // 🖼️ MÉTHODE 2 - Pour les nouveaux produits (uploads via API)
  const getProductImagesNew = (product) => {
    if (!product) return [`${REACT_BASE_URL}/images/placeholder.jpg`];
    
    // Si product.image est déjà une URL complète
    if (product.image && product.image.startsWith('http')) {
      return [product.image];
    }
    
    // Si product.image est un chemin relatif (comme /uploads/products/filename.jpg)
    if (product.image) {
      return [`${API_BASE_URL}${product.image}`];
    }
    
    // Si image_url existe
    if (product.image_url) {
      return [product.image_url.startsWith('http') 
        ? product.image_url 
        : `${API_BASE_URL}/images/products/${product.image_url.replace(/^\/+/, '')}`
      ];
    }
    
    // Si images existe (JSON)
    if (product.images) {
      try {
        const imagesArray = JSON.parse(product.images.replace(/\\"/g, '"'));
        if (imagesArray.length > 0) {
          return imagesArray.map(img => 
            img.startsWith('http') 
              ? img 
              : `${API_BASE_URL}/images/products/${img.replace(/^\/+/, '').trim()}`
          );
        }
      } catch (error) {
        console.error("Error parsing images:", error);
      }
    }
    
    // Image par défaut
    return [`${API_BASE_URL}/images/default-product.jpg`];
  };

  // 🖼️ MÉTHODE UNIFIÉE - Essaie d'abord la nouvelle méthode, puis l'ancienne
  const getProductImage = (product) => {
    if (!product) return `${REACT_BASE_URL}/images/placeholder.jpg`;
    
    // Essayer d'abord la nouvelle méthode (uploads API)
    const newMethodImages = getProductImagesNew(product);
    const newImage = newMethodImages[0];
    
    // Vérifier si l'image de la nouvelle méthode est valide (pas l'image par défaut)
    if (newImage && !newImage.includes('default-product.jpg') && !newImage.includes('placeholder.jpg')) {
      return newImage;
    }
    
    // Sinon, essayer l'ancienne méthode
    const oldMethodImages = getProductImagesOld(product);
    const oldImage = oldMethodImages[0];
    
    if (oldImage && !oldImage.includes('placeholder.jpg')) {
      return oldImage;
    }
    
    // Si aucune méthode ne fonctionne, retourner l'image par défaut de l'API
    return `${API_BASE_URL}/images/default-product.jpg`;
  };

  // 🖼️ MÉTHODE ALTERNATIVE - Basée sur le type de produit
  const getProductImageByType = (product) => {
    if (!product) return `${REACT_BASE_URL}/images/placeholder.jpg`;
    
    // Déterminer le type de produit basé sur la présence de certains champs
    const isNewProduct = product.image && product.image.startsWith('/uploads/');
    const hasApiImage = product.image && (product.image.startsWith('http') || product.image.startsWith('/uploads/'));
    
    if (hasApiImage || isNewProduct) {
      // Utiliser la nouvelle méthode pour les produits avec uploads API
      return getProductImagesNew(product)[0];
    } else {
      // Utiliser l'ancienne méthode pour les produits existants
      return getProductImagesOld(product)[0];
    }
  };

  // 🔄 État de chargement
  if (loading) {
    return (
      <div className="bg-[#fffaf3] min-h-screen flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d0b125] mx-auto mb-4"></div>
          <p className="text-[#d0b125] text-lg font-light">
            {translatedTexts.loading || "Loading products..."}
          </p>
        </div>
      </div>
    );
  }

  // ❌ État d'erreur
  if (error) {
    return (
      <div className="bg-[#fffaf3] min-h-screen flex justify-center items-center">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">
            {translatedTexts.error || error}
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-[#d0b125] text-white px-6 py-2 rounded hover:bg-[#b89a20] transition-colors"
          >
            {language === 'fr' ? 'Réessayer' : 
             language === 'ar' ? 'إعادة المحاولة' : 
             'Try Again'}
          </button>
        </div>
      </div>
    );
  }

  // Texte pour le compteur de produits
  const getProductsCountText = (count) => {
    if (language === 'fr') return `${count} produit${count > 1 ? 's' : ''}`;
    if (language === 'ar') return `${count} منتج${count > 1 ? 'ات' : ''}`;
    return `${count} product${count > 1 ? 's' : ''}`;
  };

  // Texte "NEW" selon la langue
  const getNewBadgeText = () => {
    if (language === 'fr') return 'NOUVEAU';
    if (language === 'ar') return 'جديد';
    return 'NEW';
  };

  return (
    <div className="bg-[#fffaf3] min-h-screen">
      {/* Overlay de traduction */}
      {translating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#d0b125]"></div>
            <p className="text-[#2f2a1e] font-light">
              {translatedTexts.translating || "Updating translations..."}
            </p>
          </div>
        </div>
      )}

      {/* Header Section */}
      <section className="bg-[#d0b125]/10 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl font-serif font-light text-[#2f2a1e] mb-4 tracking-wide">
            {translatedTexts.title || "All Our Products"}
          </h1>
          <p className="text-[#7a7250] text-lg font-light max-w-2xl mx-auto">
            {translatedTexts.subtitle || "Discover our natural ranges for men and women 🌿"}
          </p>
          
          {/* Indicateur de devise */}
          <div className="mt-4 text-sm text-[#7a7250]">
            {language === 'fr' && 'Prix en Euros (€)'}
            {language === 'en' && 'Prices in US Dollars ($)'}
            {language === 'ar' && 'الأسعار بالدرهم المغربي (MAD)'}
          </div>
        </motion.div>
      </section>

      {/* Categories with Products */}
      <div className="max-w-7xl mx-auto px-6 py-16 space-y-20">
        {displayCategories.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-[#7a7250] font-light text-lg">
              <svg className="w-16 h-16 mx-auto mb-4 text-[#e8e2d1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p>{translatedTexts.noProducts || "No products available."}</p>
            </div>
          </div>
        ) : (
          displayCategories.map((category, categoryIndex) => {
            // 🎯 LOGIQUE SIMPLIFIÉE POUR LES BANNIÈRES
            // Afficher la bannière femme seulement pour la première catégorie femme
            const showWomenBanner = category.gender === "women" && 
              categoryIndex === displayCategories.findIndex(cat => cat.gender === "women");
            
            // Afficher la bannière homme seulement pour la première catégorie homme
            const showMenBanner = category.gender === "men" && 
              categoryIndex === displayCategories.findIndex(cat => cat.gender === "men");

            return (
              <div key={category.id || `category-${categoryIndex}`} className="space-y-12">
                {/* Women's Banner */}
                {showWomenBanner && (
                  <motion.section
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative overflow-hidden rounded-lg"
                  >
                    <div className="relative h-64 md:h-80 overflow-hidden">
                      <img
                        src="/images/categories/women-banner.jpg"
                        alt={translatedTexts.womenBannerTitle || "Women's Collection"}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "/images/placeholder.jpg";
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-black/20" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center text-white">
                          <h2 className="text-3xl md:text-4xl font-serif font-light mb-2 tracking-wider">
                            {translatedTexts.womenBannerTitle || "Women's Collection"}
                          </h2>
                          <p className="text-sm md:text-base text-white/90 font-light">
                            {translatedTexts.womenBannerSubtitle || "Premium natural care for women"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.section>
                )}

                {/* Men's Banner */}
                {showMenBanner && (
                  <motion.section
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative overflow-hidden rounded-lg"
                  >
                    <div className="relative h-64 md:h-80 overflow-hidden">
                      <img
                        src="/images/categories/men-banner.jpg"
                        alt={translatedTexts.menBannerTitle || "Men's Collection"}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "/images/placeholder.jpg";
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-black/20" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center text-white">
                          <h2 className="text-3xl md:text-4xl font-serif font-light mb-2 tracking-wider">
                            {translatedTexts.menBannerTitle || "Men's Collection"}
                          </h2>
                          <p className="text-sm md:text-base text-white/90 font-light">
                            {translatedTexts.menBannerSubtitle || "Premium natural care for men"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.section>
                )}

                {/* Product Section */}
                <motion.section
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <div className="text-center mb-12">
                    <h2 className="text-3xl font-serif font-light text-[#2f2a1e] tracking-wider inline-block border-b-2 border-[#d0b125] pb-3">
                      {category.gender === "women"
                        ? `${translatedTexts.womenBannerTitle?.replace("Collection", "") || "Women's"} ${category.name}`
                        : category.gender === "men"
                        ? `${translatedTexts.menBannerTitle?.replace("Collection", "") || "Men's"} ${category.name}`
                        : category.name}
                    </h2>
                    <p className="text-[#7a7250] mt-2">
                      {getProductsCountText(category.products.length)}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                    {category.products.length > 0 ? (
                      category.products.map((product, index) => (
                        <motion.div
                          key={product.id || `product-${index}`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="group flex flex-col h-full"
                        >
                          <div className="bg-white rounded-none shadow-sm border border-[#e8e2d1] hover:shadow-lg transition-all duration-500 group-hover:border-[#d0b125]/30 flex flex-col h-full overflow-hidden">
                            <Link
                              to={`/product/${product.id}`}
                              className="block flex-grow flex-col"
                            >
                              <div className="relative overflow-hidden bg-gray-50 aspect-square flex-shrink-0">
                                <img
                                  src={getProductImage(product)}
                                  alt={product.name}
                                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                  onError={(e) => {
                                    // Fallback vers l'autre méthode en cas d'erreur
                                    const fallbackImage = getProductImageByType(product);
                                    if (fallbackImage !== e.target.src) {
                                      e.target.src = fallbackImage;
                                    } else {
                                      e.target.src = `${API_BASE_URL}/images/default-product.jpg`;
                                    }
                                  }}
                                />
                                {product.featured && (
                                  <div className="absolute top-3 left-3 bg-[#d0b125] text-white text-xs px-3 py-1 tracking-wide font-light">
                                    {getNewBadgeText()}
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-500" />
                              </div>

                              <div className="p-6 flex flex-col flex-grow">
                                <h3 className="font-serif text-lg text-[#2f2a1e] mb-3 leading-tight tracking-wide text-center line-clamp-2 min-h-[3rem] flex items-center justify-center">
                                  {product.name}
                                </h3>

                                <div className="mt-auto pt-4 border-t border-[#e8e2d1]">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[#d0b125] text-xl font-light tracking-wider">
                                      {formatPrice(product.price, 'MAD')}
                                    </span>
                                    <button
                                      onClick={(e) => handleAddToCart(product, e)}
                                      className="bg-[#2f2a1e] text-white text-sm px-5 py-2 hover:bg-[#d0b125] transition-all duration-300 tracking-wide font-light min-w-[100px] text-center"
                                    >
                                      {translatedTexts.addButton || "Add to Cart"}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </Link>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="col-span-full text-center py-12">
                        <div className="text-[#7a7250] font-light text-lg">
                          <svg className="w-16 h-16 mx-auto mb-4 text-[#e8e2d1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m8-8V4a1 1 0 00-1-1h-2a1 1 0 00-1 1v1M9 7h6" />
                          </svg>
                          <p>
                            {translatedTexts.noProducts || "No products available in this category yet."}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.section>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}